import { useQueryClient } from '@tanstack/solid-query';
import { createMemo, type JSX } from 'solid-js';
import { AgentsSkeleton } from '../components/AgentsSkeleton';
import { AgentsView } from '../components/AgentsView';
import { PageHeader } from '../components/PageHeader';
import { QueryBoundary, type QueryLike } from '../components/QueryBoundary';
import { Toast } from '../components/Toast';
import { t } from '../i18n';
import { useAgentActivity } from '../lib/agent-activity';
import { createModeSaves } from '../lib/agent-mode-save';
import { createModelSaves } from '../lib/agent-model-save';
import { useAgentModes } from '../lib/agent-modes';
import { AGENTS_HISTORY_QUERY_KEY } from '../lib/agents-history';
import { useAgentsPreview } from '../lib/agents-preview';
import { useKeys } from '../lib/keys';
import { useLlmRoutes } from '../lib/llmRoutes';
import { useMyKeys } from '../lib/my-keys';
import { createRepoRouteSaves } from '../lib/repo-route-save';
import { useRepoList } from '../lib/repos';
import { canAdminWrite, roleSettled } from '../lib/roles';
import { SESSION_TOKEN, useMe } from '../lib/session';
import { createToast, type ToastAction } from '../lib/toast';

// The Agents page (#5209, epic #5200) — each agent's model pick, first in WATCH.
// Container: it owns every read and the model-save controller (#5210,
// `lib/agent-model-save.ts`) with its toast; `AgentsView` folds and paints. The
// drawer is #5211.
//
// WHO MAY PIN: `write:billing`, an admin-ceiling scope — `canAdminWrite`, shut until
// THIS org's `/v1/me` answered (`roleSettled`, never a prior org's placeholder). A
// UX hint only: the API's 403 arbitrates, and the card turns it into the lock state.
//
// FOUR INDEPENDENT READS, one gate: the org's route PINS (`useLlmRoutes`, the
// same `read:billing` scope every card's trigger needs) is the page's primary,
// gating query — the loading skeleton, the denied wall and the retryable error
// box all come from it. Keys, activity and the llm_route preview each degrade
// on their OWN (Card anatomy: preview failure -> "Couldn't check right now",
// activity failure -> "—"), so a stall in any one of them never blanks a page
// whose eight cards are otherwise still real.

export function Agents(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const routes = useLlmRoutes(token);
  const keys = useKeys(token);
  const myKeys = useMyKeys(token);
  const activity = useAgentActivity(token);
  const preview = useAgentsPreview(token);
  const me = useMe(token);
  const { toast, notify, dismiss } = createToast();
  // `dismiss` too: the controller takes the toast down on an org switch — `OrgScope`
  // re-keys without unmounting this page, and an Undo left up would target org A's
  // row from org B's page (it is refused unsent either way).
  const client = useQueryClient();
  const saves = createModelSaves({
    client,
    token,
    notify,
    dismissNotice: dismiss,
  });
  const canEdit = (): boolean => roleSettled(me) && canAdminWrite(me.data?.role);
  // `write:prompts` is admin-ceiling too (`lib/roles.ts` — every ADMIN_SCOPES
  // member, billing included), so it is the SAME predicate as `canEdit` today.
  // Kept as its own name/call so the two concerns (route pins vs modes/tools)
  // can diverge later without a second audit of every call site (#5211).
  const canEditModes = canEdit;
  // Every card's changed-by chip reads `/v1/agents/history`, and a mode toggle or
  // a repo pin is a change it names, so both re-reads below stale it too. The model
  // save does the same inside `createModelSaves`; before 19-7 only that one did, and
  // the chip named the previous change until a reload.
  const rereadHistory = (): Promise<void> =>
    client.invalidateQueries({ queryKey: [AGENTS_HISTORY_QUERY_KEY] });

  // The org's resolved agent modes — the Advanced drawer's tools checklist and
  // each mode-backed card's switch (#5211). Gated on `canEditModes` (every GET
  // here needs `write:prompts`): the Permissions section states the GET is
  // fetched only when the caller holds the scope, so a member/viewer's browser
  // never sends a request the server would only 403. `canEditModes` is read
  // REACTIVELY by `useAgentModes` (an accessor, not a snapshot), so the query
  // turns on once `/v1/me` settles the role — never stuck on its value at mount.
  const modes = useAgentModes(() => ({ kind: 'org' }), token, canEditModes);
  const modeSaves = createModeSaves({
    scope: { kind: 'org' },
    token,
    // The card's pill reads `activity.mode_enabled` (the 30s activity poll), the
    // switch beside it reads the modes query: refetch BOTH after a save, or the
    // pill keeps saying "Running" / "Off" for up to a poll after the user's own
    // toggle (#5624).
    refetch: () => Promise.all([modes.refetch(), activity.refetch(), rereadHistory()]),
    notify,
    dismissNotice: dismiss,
  });
  const repoSaves = createRepoRouteSaves({
    token,
    refetch: () => Promise.all([routes.refetch(), rereadHistory()]),
    notify,
    dismissNotice: dismiss,
  });
  // Repo id → full_name, for the drawer's Per-repo section headings. Enabled
  // repos only (`useRepoList`), same population every other picker offers.
  const repoList = useRepoList(token);
  const repoNames = createMemo(
    () => new Map(repoList.rows().map((r) => [r.id, r.full_name] as const)),
  );
  const onKeyRenamed = (): void => {
    void keys.refetch();
    void routes.refetch();
  };

  // The gate reads the routes query's STATE, never its rows. `QueryBoundary`
  // re-runs its children on every `data` change, which would REMOUNT all nine
  // cards on each save's optimistic paint and again on its re-read — replacing
  // the very picker trigger focus has just been returned to (#5210's keyboard
  // story). `data` here is a stable `true` once the rows exist; the rows reach
  // `AgentsView` through a getter, so a save patches the cards in place. A MEMO,
  // not a bare getter: a getter reading `routes.data` would still subscribe the
  // boundary to every row change; the memo notifies only when "loaded" flips.
  const loaded = createMemo(() => routes.data !== undefined);
  const gate: QueryLike<true> = {
    get isLoading() {
      return routes.isLoading;
    },
    get isError() {
      return routes.isError;
    },
    get error() {
      return routes.error;
    },
    get data() {
      return loaded() ? true : undefined;
    },
    refetch: () => routes.refetch(),
  };

  return (
    <section class="page agents">
      {/* No `eyebrow`: PageHeader reserves that slot's pulsing dot for a
          live-streamed view (its own doc comment), and this page is polled,
          not streamed. The mono "AGENTS" kicker the design calls for is
          visual polish deferred, not a live-status claim to fake. */}
      <PageHeader title={t('agents.title')} desc={t('agents.subtitle')} />
      <QueryBoundary query={gate} loading={<AgentsSkeleton />}>
        {() => (
          <AgentsView
            routes={routes.data ?? []}
            hasPersonalKeys={(myKeys.data ?? []).length > 0}
            keys={{ data: keys.data, isError: keys.isError }}
            activity={{ data: activity.data, isError: activity.isError }}
            preview={{ data: preview.data, isError: preview.isError }}
            canEdit={canEdit()}
            saves={saves}
            modes={modes.data}
            canEditModes={canEditModes()}
            modeSaves={modeSaves}
            repoNames={repoNames()}
            repoSaves={repoSaves}
            token={token}
            onKeyRenamed={onKeyRenamed}
          />
        )}
      </QueryBoundary>
      <Toast
        message={toast()?.message ?? null}
        tone={toast()?.tone ?? 'success'}
        {...(toast()?.action === undefined ? {} : { action: toast()?.action as ToastAction })}
        onDismiss={dismiss}
      />
    </section>
  );
}
